import { createClient } from '@urql/core';
import { authExchange } from '@urql/exchange-auth';
import { retryExchange } from '@urql/exchange-retry';
import { errorExchange, fetchExchange, SSRExchange, subscriptionExchange } from '@urql/next';
import { SubscribePayload } from 'graphql-ws';

import { graphqlUrl } from 'config/endpoints';
import { buildId, isPreview } from 'helpers/environment';
import handleUrqlError from 'helpers/handleUrqlError';
import { getWsClient } from 'helpers/wsClient';
import { Nullable } from 'types/global';

interface Props {
  ssr?: SSRExchange;
  getCookies?: () => Nullable<string> | Promise<Nullable<string>>;
  authCookie?: string;
}

const DEFAULT_REQUEST_HEADERS = {
  'X-NullifyRisk-Client': 'webapp',
  'X-NullifyRisk-Client-Version': buildId ?? 'unknown',
};

export const makeUrqlClient = (props?: Props) => {
  const { ssr, getCookies, authCookie } = props ?? {};

  return createClient({
    url: graphqlUrl,
    fetchOptions: {
      credentials: 'include',
      cache: 'no-store',
    },
    exchanges: [
      // see https://formidable.com/open-source/urql/docs/advanced/subscriptions/#setting-up-graphql-ws
      subscriptionExchange({
        forwardSubscription: (operation) => ({
          subscribe: (sink) => ({
            unsubscribe: getWsClient().subscribe(operation as SubscribePayload, sink),
          }),
        }),
      }),
      authExchange(async (utils) => {
        let cookies = '';
        if (getCookies != null) {
          cookies = (await getCookies()) ?? '';
        }

        return {
          addAuthToOperation(operation) {
            return utils.appendHeaders(operation, {
              Cookie: cookies,
              // This is a temporary workaround around the fact that with preview apps, we use vercel's autogenerated domain names
              // making CORS requests from the frontend to the backend quite difficult.
              //
              // So, we pass the auth cookie in a custom header to the backend.
              'X-NullifyRisk-Auth-Token': isPreview ? (authCookie ?? '') : '',
              ...DEFAULT_REQUEST_HEADERS,
            });
          },
          willAuthError: (_operation) => {
            if (cookies === '') {
              return true;
            }
            return false;
          },
          didAuthError: (error, _operation) => {
            return error.graphQLErrors.some((e) => e.extensions?.code === 'FORBIDDEN');
          },
          refreshAuth: () => Promise.resolve(),
        };
      }),
      errorExchange({
        onError: (error, operation) => {
          handleUrqlError(error, operation);
        },
      }),
      retryExchange({
        initialDelayMs: 1000,
        maxDelayMs: 5000,
        maxNumberAttempts: 2,
        retryIf: (err) => err.networkError != null,
      }),
      ...(ssr ? [ssr] : []),
      fetchExchange,
    ],
  });
};
